import { format } from 'date-fns'
import * as Linking from 'expo-linking'
import { useEffect, useState } from 'react'
import { View } from 'react-native'
import { Tooltip } from 'react-native-paper'
import { CustomerInfo } from 'react-native-purchases'
import { StyleSheet } from 'react-native-unistyles'

import Card from '@/components/shared/Card'
import UniButton from '@/components/unistyles/UniButton'
import UniDivider from '@/components/unistyles/UniDivider'
import UniText from '@/components/unistyles/UniText'
import { useAppStore } from '@/store/useAppStore'
import {
  getTierLimitText,
  getTierName,
  isWithinTierLimit,
} from '@/utils/purchases/billing'
import purchases from '@/utils/purchases/purchases'
import trpc from '@/utils/trpc'

const OrganizationSubscriptionCard = () => {
  const currentOrganization = useAppStore.use.currentOrganization().organization
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo | null>(null)

  const fosterCountQuery = trpc.foster.currentMonthFosterCount.useQuery(
    {
      organizationId: currentOrganization?.id || '',
    },
    {
      enabled: !!currentOrganization?.id,
    }
  )

  useEffect(() => {
    const organizationId = currentOrganization?.id
    if (!organizationId) {
      return
    }

    let cancelled = false
    // Don't keep showing the previous org's subscription while this one loads
    setCustomerInfo(null)

    async function preload(id: string) {
      try {
        const ci = await purchases.getCustomerInfo(id)
        if (ci && !cancelled) {
          setCustomerInfo(ci)
        }
      } catch {
        // The card stays empty; the subscription check already logs RevenueCat failures
      }
    }

    preload(organizationId)

    return () => {
      cancelled = true
    }
  }, [currentOrganization?.id])
  const fosterCount = fosterCountQuery.data?.count ?? 0
  const currentTier = currentOrganization?.subscriptionTier
  const statusColor = isWithinTierLimit(fosterCount, currentTier)
    ? 'successColor'
    : 'errorColor'

  const entitlements = customerInfo?.entitlements.active
  const isSubscribed = !!(
    entitlements?.['small-rescue']?.isActive ||
    entitlements?.['medium-rescue']?.isActive ||
    entitlements?.['large-rescue']?.isActive
  )
  const isComped = isSubscribed && !customerInfo?.managementURL
  const managementURL = customerInfo?.managementURL || ''

  // Get subscription dates from the active entitlement
  const activeEntitlement = Object.values(entitlements || {}).find(
    (e) => e.isActive
  )
  const subscriptionStartDate = activeEntitlement?.originalPurchaseDate
    ? format(new Date(activeEntitlement.originalPurchaseDate), 'MMM d, yyyy')
    : null
  const nextBillingDate = activeEntitlement?.expirationDate
    ? format(new Date(activeEntitlement.expirationDate), 'MMM d, yyyy')
    : null

  const handleManageSubscription = () => {
    if (managementURL) {
      Linking.openURL(managementURL)
    }
  }

  return (
    <Card title="Subscription Status">
      <View style={styles.statsContainer}>
        <View style={styles.stat}>
          <UniText style={styles.statLabel}>Current Plan</UniText>
          <UniText style={styles.statValue}>{getTierName(currentTier)}</UniText>
          <UniText style={styles.statDetail}>
            {getTierLimitText(currentTier)}
          </UniText>
        </View>
        <View style={styles.stat}>
          <UniText style={styles.statLabel}>Current Usage</UniText>
          <UniText style={[styles.statValue, styles[statusColor]]}>
            {fosterCount} fosters
          </UniText>
          <UniText style={styles.statDetail}>This month</UniText>
        </View>
      </View>

      <UniDivider style={styles.divider} />

      <View style={styles.statusContainer}>
        <UniText style={styles.sectionTitle}>Status</UniText>
        {currentOrganization?.subscriptionActive ? (
          <>
            <UniText style={[styles.statusMessage, styles.successColor]}>
              Your subscription is active and in good standing
            </UniText>
            {subscriptionStartDate && (
              <UniText style={styles.statusDetail}>
                Member since: {subscriptionStartDate}
              </UniText>
            )}
            {nextBillingDate && (
              <UniText style={styles.statusDetail}>
                Next billing date: {nextBillingDate}
              </UniText>
            )}
          </>
        ) : (
          <UniText style={[styles.statusMessage, styles.errorColor]}>
            Your organization has exceeded the free tier limit. Please upgrade
            your subscription to continue.
          </UniText>
        )}
      </View>

      {isSubscribed && !isComped ? (
        managementURL ? (
          <UniButton
            icon="cog"
            mode="contained"
            onPress={handleManageSubscription}
            style={styles.button}
          >
            Manage Subscription
          </UniButton>
        ) : null
      ) : !isSubscribed ? (
        <UniButton
          icon="star"
          mode="contained"
          onPress={() => purchases.showPaywall()}
          style={styles.button}
        >
          Subscribe
        </UniButton>
      ) : (
        <Tooltip title="This is a complimentary subscription that cannot be managed">
          <View>
            <UniButton
              disabled
              icon="cog"
              mode="contained"
              style={styles.button}
            >
              Manage Subscription
            </UniButton>
          </View>
        </Tooltip>
      )}
    </Card>
  )
}

const styles = StyleSheet.create((theme) => ({
  button: {
    marginTop: theme.tokens.spacing[2],
  },
  divider: {
    marginVertical: theme.tokens.spacing[4],
  },
  errorColor: {
    color: theme.colors.error,
  },
  sectionTitle: {
    color: theme.colors.onSurface,
    fontWeight: '600',
    marginBottom: theme.tokens.spacing[2],
    opacity: 0.8,
  },
  stat: {
    flex: 1,
  },
  statDetail: {
    color: theme.colors.onSurfaceVariant,
    fontSize: 13,
    marginTop: 2,
    opacity: 0.7,
  },
  statLabel: {
    color: theme.colors.onSurfaceVariant,
    fontSize: 13,
    fontWeight: '600',
    marginBottom: theme.tokens.spacing[1],
    textTransform: 'uppercase',
  },
  statValue: {
    fontSize: 20,
    fontWeight: '600',
  },
  statsContainer: {
    flexDirection: 'row',
    gap: theme.tokens.spacing[4],
    marginBottom: theme.tokens.spacing[4],
  },
  statusContainer: {
    marginBottom: theme.tokens.spacing[4],
  },
  statusDetail: {
    color: theme.colors.onSurfaceVariant,
    fontSize: 13,
    marginTop: theme.tokens.spacing[1],
  },
  statusMessage: {
    fontSize: 15,
    lineHeight: 20,
  },
  successColor: {
    color: theme.colors.primary,
  },
}))

export default OrganizationSubscriptionCard
